import React, { Fragment, useContext, useEffect, useState } from 'react';
import CustomPopup from './CustomPopup';
import IUserList from '../../utils/UserList/UserList.interface';
import { EditUserForm } from '../AdminPanel/EditUser';
import { DeleteUser } from '../AdminPanel/DeleteUser';
import ResetMfa from './ResetMfa';
import ConditionalLoader from '../ConditionalLoader';
import EyeToggle from '../../library/Button/EyeToggle';
import LatchButton from '../../library/Button/LatchButton';
import DeactiveUser from '../../utils/Deactivate/DeactivateUser';
import SwitchUserToAdmin from '../../utils/SwitchUser/SwitchUser';
import { responseMessages } from '../../utils/Helper/enums';
import { handleApiResponse } from '../../utils/Helper/apiResponseHandler';

/**
 * This common props takes in several parameters.
 * @usersData - These prop is likely used to store the users list received from back-end.
 * @isLoading - This prop is  used to implement the loader while data is being fetched from back-end.
 * @query - These prop is used to implement search functionality based on user's input
 * @show - These prop is used to show the userlist popup.
 * @setShow - These prop is used to update the @show based on user's input
 * @disabled - This prop is used to pass the disabled field in edituser and delete user form.
 **/

interface OrgUserProps {
  usersData: any[];
  query: string | null;
  disabled: boolean;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  onDeleteUser: (email: string) => void;
  onEditUser: (updatedUser: any) => void;
  isPartnerActive: boolean;
  onRefreshUsers: () => void;
  onSwitchAdmin: (newAdminEmail: string) => void;
}
// component for showing Organization users for a specific Organization Admin.
export const OrgUserForm: React.FC<OrgUserProps> = ({
  usersData,
  query,
  disabled,
  show,
  setShow,
  isLoading,
  onDeleteUser,
  onEditUser,
  isPartnerActive,
  onRefreshUsers,
  onSwitchAdmin
}) => {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [switchedAdmins, setSwitchedAdmins] = useState<Record<string, boolean>>({});
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      const initialVisibility: Record<string, boolean> = {};
      usersData.forEach((user) => {
        initialVisibility[user.email] = user.isActive;
      });
      setIsVisible(initialVisibility);
    }
  }, [usersData, show]);

  const handleSwitchToAdmin = async (email: string) => {
    setSwitchedAdmins((prev) => ({ ...prev, [email]: true }));
    setAlertMessage('Please wait...');
    setShowAlert(true);
    setLoadingToggle(true);
    try {
      const switchUser = new SwitchUserToAdmin({ email });
      const result = await switchUser.switchUserToAdmin();
      setAlertMessage(result.body);
      if (result.statusCode === 201) {
        setPendingRefresh(true);
        setNewAdminEmail(email);
      } else {
        setSwitchedAdmins((prev) => ({ ...prev, [email]: false }));
      }
    } catch (error) {
      console.error('Error switching user to admin:', error);
      setSwitchedAdmins((prev) => ({ ...prev, [email]: false }));
      setAlertMessage('Error switching user to admin');
    } finally {
      setLoadingToggle(false);
    }
  };

  const handleDeactivateUser = async (email: string) => {
    const newState = !isVisible[email];
    if (!isPartnerActive && newState) {
      setAlertMessage('Sorry, can not activate user as organisation is inactive ☹️!!');
      setShowAlert(true);
      return;
    }
    setLoadingToggle(true);
    setIsVisible((prev) => ({
      ...prev,
      [email]: newState
    }));

    try {
      const userObj = new DeactiveUser({
        email,
        isActive: newState
      });

      const updateStatus = await userObj.deactivateUser();
      const errorMessage = responseMessages.SomethingWentWrong;
      const successMessage = updateStatus.body;
      const response = handleApiResponse(updateStatus.statusCode, errorMessage, successMessage);
      const responseBody = response.body;
      // Update parent usersData state
      const updatedUser = usersData.find((user) => user.email === email);
      if (updatedUser) {
        onEditUser({ ...updatedUser, isActive: newState });
      }
      setAlertMessage(responseBody);
      setShowAlert(true);
    } catch (error) {
      console.error('Error deactivating user:', error);
      setAlertMessage('Error deactivating user');

      // Optionally revert local UI
      setIsVisible((prev) => ({
        ...prev,
        [email]: !newState
      }));
    } finally {
      setLoadingToggle(false);
    }
  };

  const triggerRefresh = () => {
    if (newAdminEmail) {
      onSwitchAdmin(newAdminEmail);
      setNewAdminEmail(null);
    }
    setSwitchedAdmins({});
    onRefreshUsers();
    setPendingRefresh(false);
  };

  const alertCloseHandler = () => {
    setShowAlert(false);
    if (pendingRefresh) {
      triggerRefresh();
    }
  };

  // Method to close the popup on clicking the close button
  const popupCloseHandler = (e: boolean) => {
    setShow(e);
    setShowAlert(e);
    if (!e && pendingRefresh) {
      triggerRefresh();
    }
  };

  return (
    <CustomPopup onClose={popupCloseHandler} show={show} width={'w-41%'}>
      <div className="w-fit h-fit">
        <Fragment>
          <div className="flex start justify-between px-4 py-4 border-b border-black">
            <h4 className="mb-0 leading-normal text-2xl text-black">Partner Users</h4>
          </div>
          <ConditionalLoader isLoading={isLoading} className="flex justify-center" width={200}>
            <div className="grow shrink basis-auto px-0 sm:p-4 block max-h-[30rem] overflow-y-auto">
              {usersData.length > 0 ? (
                <table className="w-100%  border-collapse ">
                  <thead className="table-header-group align-middle bg-[#f8f8f8] ">
                    <tr className="text-black text-left table-row align-inherit">
                      <th className="font-semibold text-sm uppercase px-2 sm:px-6 py-4 text-center">
                        Users Name
                      </th>
                      <th className="font-semibold text-sm uppercase px-2 sm:px-6 py-4 text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData
                      .filter((user: any) =>
                        user.name?.toLowerCase().includes(query?.toLowerCase() ?? '')
                      )
                      .map((dataSet: IUserList, index: number) => {
                        return (
                          <tr key={index}>
                            <td className="px-2 sm:px-6 py-4 text-center">
                              <div className="w-48">
                                <p className="text-ellipsis overflow-hidden">{dataSet.email}</p>
                              </div>
                            </td>
                            <td className="flex px-4 sm:px-6 py-4 text-center">
                              {' '}
                              <div
                                className={`flex items-center w-fit pb-1 min-w-fit gap-[2px] ${
                                  loadingToggle ? 'pointer-events-none cursor-wait' : ''
                                }`}>
                                <EditUserForm
                                  orgPartnerCompanyEmail={dataSet.orgEmail}
                                  userName={dataSet.name}
                                  orgJobTitle={dataSet.role}
                                  orgPartnerCompanyName={dataSet.companyName}
                                  userEmail={dataSet.email}
                                  disabled={disabled}
                                  userPhone={dataSet.phone}
                                  onEditUser={onEditUser}
                                />
                                <DeleteUser
                                  email={dataSet.email ?? ''}
                                  orgEmail={dataSet.orgEmail ?? ''}
                                  disabled={disabled}
                                  onDeleteUser={onDeleteUser}
                                />
                                <ResetMfa userEmail={dataSet.email} disabled={disabled} />
                                <EyeToggle
                                  onClick={() => {
                                    void handleDeactivateUser(dataSet.email!);
                                  }}
                                  isVisible={!dataSet.isActive}
                                  disabled={!isPartnerActive && !dataSet.isActive}
                                />
                                <LatchButton
                                  isAdmin={
                                    dataSet.role === 'Admin' || !!switchedAdmins[dataSet.email!]
                                  }
                                  onClick={() => {
                                    void handleSwitchToAdmin(dataSet.email!);
                                  }}
                                  disabled={disabled || loadingToggle}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              ) : (
                <div className="px-2 py-2 text-center pt-4 font-bold">
                  <h1 className="text-bold">No users found under this partner ☹️ !!</h1>
                </div>
              )}
            </div>
          </ConditionalLoader>
        </Fragment>
      </div>
      <CustomPopup onClose={alertCloseHandler} show={showAlert} width={'w-41%'}>
        <div className="max-h-30%">
          <p className="mr-0 mb-0.5 mt-4 text-[17px]">{alertMessage}</p>
        </div>
      </CustomPopup>
    </CustomPopup>
  );
};
